import { Shield, Clock, Euro } from 'lucide-react'
import { SERVICE_PRICES, SERVICE_LABELS } from '~/lib/programmatic-seo'

/**
 * Shows honest service info for a city+service combination.
 * No fake numbers — real value propositions + price range.
 */

interface DemandCounterProps {
  cityName: string
  serviceName: string
  variant?: 'full' | 'compact' | 'banner'
}

export function DemandCounter({ cityName, serviceName, variant = 'full' }: DemandCounterProps) {
  // Find price for this service
  const serviceSlug = Object.entries(SERVICE_LABELS).find(([_, v]) => v.name === serviceName)?.[0] ?? ''
  const price = SERVICE_PRICES[serviceSlug]

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Shield className="h-3.5 w-3.5 text-emerald-500" />
        Artisans {serviceName.toLowerCase()} vérifiés à {cityName}
      </div>
    )
  }

  if (variant === 'banner') {
    return (
      <div className="flex items-center justify-center gap-4 rounded-xl bg-primary-50 px-4 py-3 text-xs">
        <span className="flex items-center gap-1.5 font-medium text-primary">
          <Shield className="h-3.5 w-3.5" /> Artisans vérifiés à {cityName}
        </span>
        {price && (
          <span className="text-slate-600">Prix moyen : <strong>{price.min}–{price.max} €</strong>/{price.unit}</span>
        )}
        <span className="flex items-center gap-1 text-slate-500">
          <Clock className="h-3 w-3" /> Devis sous 48h
        </span>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
      <h3 className="text-sm font-bold text-slate-900">{serviceName} à {cityName}</h3>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-blue-50 p-3 text-center">
          <Shield className="mx-auto h-5 w-5 text-blue-600" />
          <p className="mt-1 text-sm font-bold text-blue-800">100%</p>
          <p className="text-[10px] text-blue-500">Artisans vérifiés</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3 text-center">
          <Euro className="mx-auto h-5 w-5 text-amber-600" />
          <p className="mt-1 text-sm font-bold text-amber-800">{price ? `${price.min}–${price.max} €` : 'Sur devis'}</p>
          <p className="text-[10px] text-amber-500">Prix moyen</p>
        </div>
        <div className="rounded-xl bg-emerald-50 p-3 text-center">
          <Clock className="mx-auto h-5 w-5 text-emerald-600" />
          <p className="mt-1 text-sm font-bold text-emerald-800">48h</p>
          <p className="text-[10px] text-emerald-500">Délai réponse</p>
        </div>
      </div>
    </div>
  )
}
